import {CommunityAgentScope} from "../types/CommunityAgentScope";
import {IAgentAvatarIdentity} from "./types/IAgentAvatarIdentity";
import {IAgentAvatarShape} from "./types/IAgentAvatarShape";

export class AgentAvatarBody {

    public static VIEW_MIN = -10;
    public static VIEW_SIZE = 220;
    public static VIEW_BOX = `${AgentAvatarBody.VIEW_MIN} ${AgentAvatarBody.VIEW_MIN} ${AgentAvatarBody.VIEW_SIZE} ${AgentAvatarBody.VIEW_SIZE}`;

    private static images: Map<string, string> = new Map();
    private static shapes: Map<string, IAgentAvatarShape> = new Map();
    private static boxes: { [shape: string]: number[] } = {
        hex: [42.8, 47.5, 157.2, 164.5],
        pebble: [26.6, 50.9, 173.4, 161.3],
        bubble: [40, 44, 160, 169.6],
        gem: [42, 50, 158, 158.2],
        shield: [40, 38, 160, 174],
        blob: [35.2, 41.3, 169.1, 163]
    };

    public static IDENTITIES: IAgentAvatarIdentity[] = [
        {persona: CommunityAgentScope.PERSONA_NOX, color: "#e8b04b", shape: "hex"},
        {persona: CommunityAgentScope.PERSONA_MAYA, color: "#f0a875", shape: "pebble"},
        {persona: CommunityAgentScope.PERSONA_ALYA, color: "#e85d5d", shape: "bubble"},
        {persona: CommunityAgentScope.PERSONA_REMI, color: "#a06ee0", shape: "gem"},
        {persona: CommunityAgentScope.PERSONA_MARC, color: "#7bb0e8", shape: "shield"},
        {persona: CommunityAgentScope.PERSONA_LOUISE, color: "#c9c9d0", shape: "blob"},
        {persona: CommunityAgentScope.PERSONA_LUCA, color: "#4fc3d0", shape: "square"},
        {persona: CommunityAgentScope.PERSONA_JULE, color: "#3fa877", shape: "house"},
        {persona: CommunityAgentScope.PERSONA_CHRIS, color: "#ec6fa0", shape: "capsule"},
        {persona: CommunityAgentScope.PERSONA_MAX, color: "#9ad45a", shape: "drop"}
    ];

    public static identity(persona: number): IAgentAvatarIdentity {
        return this.IDENTITIES.find(identity => identity.persona == persona)
            ?? this.IDENTITIES.find(identity => identity.persona == CommunityAgentScope.PERSONA_ALYA);
    }

    /**
     * Ce qu'il faut pour que le corps d'un agent remplisse une part donnée de son carré, centré : les formes n'occupent
     * pas toutes la même place dans le dessin, un galet large paraissait plus gros qu'une gemme à cadre égal.
     */
    public static fit(identity: IAgentAvatarIdentity, share: number): { scale: number, shiftX: number, shiftY: number } {
        const box = this.boxOf(identity.shape);
        const width = box[2] - box[0];
        const height = box[3] - box[1];
        const scale = share * this.VIEW_SIZE / Math.max(width, height);
        const center = this.VIEW_MIN + this.VIEW_SIZE / 2;
        return {
            scale: scale,
            shiftX: (center - (box[0] + box[2]) / 2) / this.VIEW_SIZE * scale,
            shiftY: (center - (box[1] + box[3]) / 2) / this.VIEW_SIZE * scale
        };
    }

    public static toPercent(value: number): string {
        return ((value - this.VIEW_MIN) / this.VIEW_SIZE * 100).toFixed(2) + "%";
    }

    public static shape(name: string): IAgentAvatarShape {
        if (this.shapes.size == 0) {
            this.buildShapes();
        }
        return this.shapes.get(name);
    }

    public static image(identity: IAgentAvatarIdentity): string {
        let cacheKey = identity.persona + ":" + identity.shape + ":" + identity.color;
        if (!this.images.has(cacheKey)) {
            this.images.set(cacheKey, "data:image/svg+xml;charset=utf-8," + encodeURIComponent(this.svg(identity)));
        }
        return this.images.get(cacheKey);
    }

    private static svg(identity: IAgentAvatarIdentity): string {
        let shape = this.shape(identity.shape);
        let box = this.boxOf(identity.shape);
        let path = shape.path;
        let color = identity.color;
        let centerX = (box[0] + box[2]) / 2;
        let centerY = (box[1] + box[3]) / 2;
        let size = Math.max(box[2] - box[0], box[3] - box[1]);
        let light = this.hueShade(color, 14, 12, 4);
        let deep = this.hueShade(color, -12, -12, 8);

        return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${this.VIEW_BOX}" width="440" height="440">
            <defs>
                <clipPath id="c"><path d="${path}"/></clipPath>
                <linearGradient id="g" x1="0" y1="0" x2=".75" y2="1"><stop offset="0" stop-color="${light}"/><stop offset="1" stop-color="${deep}"/></linearGradient>
                <filter id="L" x="-40%" y="-40%" width="180%" height="180%"><feGaussianBlur stdDeviation="${(size * .09).toFixed(1)}"/></filter>
                <filter id="S" x="-40%" y="-40%" width="180%" height="180%"><feGaussianBlur stdDeviation="1.4"/></filter>
                <filter id="X" x="-60%" y="-60%" width="220%" height="220%"><feGaussianBlur stdDeviation="${(size * .12).toFixed(1)}"/></filter>
                <filter id="A" x="-30%" y="-30%" width="160%" height="160%"><feDropShadow dx="0" dy="2" stdDeviation="1.3" flood-color="#000" flood-opacity=".28"/></filter>
            </defs>
            <path d="${path}" fill="${deep}" filter="url(#X)" opacity=".3" transform="${this.scaledAround(centerX, centerY, .9, 0, 4)}"/>
            <path d="${path}" fill="url(#g)"/>
            <g clip-path="url(#c)">
                <path d="${path}" fill="#fff" filter="url(#L)" opacity=".3" transform="${this.scaledAround(centerX, centerY, .55, -10, -18)}"/>
                <path d="${path}" fill="none" stroke="${deep}" stroke-width="${(size * .16).toFixed(0)}" filter="url(#L)" opacity=".5" transform="translate(-4 -8)"/>
                <path d="${path}" fill="none" stroke="#fff" stroke-width="2.5" filter="url(#S)" opacity=".35" transform="translate(2 3)"/>
            </g>
            <g filter="url(#A)">${this.accessory(identity.persona, this.hueShade(color, 0, -22), shape)}</g>
        </svg>`;
    }

    private static accessory(persona: number, color: string, shape: IAgentAvatarShape): string {
        let rightEyeX = shape.eyeX + 20;
        switch (persona) {
            case CommunityAgentScope.PERSONA_NOX:
                return `<path d="M100 44 V22" stroke="${color}" stroke-width="5" stroke-linecap="round"/><circle cx="100" cy="17" r="7" fill="${color}"/>`;
            case CommunityAgentScope.PERSONA_MAYA:
                return `<g fill="none" stroke="${color}" stroke-width="5"><rect x="134" y="30" width="30" height="16" rx="8" transform="rotate(-35 149 38)"/><rect x="150" y="44" width="30" height="16" rx="8" transform="rotate(-35 165 52)"/></g>`;
            case CommunityAgentScope.PERSONA_ALYA:
                return `<g transform="rotate(35 160 44)"><rect x="155" y="18" width="11" height="36" rx="3" fill="${color}"/><path d="M155 54 L160.5 66 L166 54 Z" fill="${color}"/></g>`;
            case CommunityAgentScope.PERSONA_REMI:
                return `<path d="M156 18 Q158.5 32 172 34 Q158.5 36 156 50 Q153.5 36 140 34 Q153.5 32 156 18 Z" fill="${color}"/><circle cx="176" cy="56" r="4" fill="${color}"/>`;
            case CommunityAgentScope.PERSONA_MARC:
                return `<circle cx="${rightEyeX}" cy="${shape.eyeY}" r="17" fill="none" stroke="${color}" stroke-width="4.5"/><path d="M${rightEyeX + 12} ${shape.eyeY + 12} Q${rightEyeX + 26} ${shape.eyeY + 36} ${rightEyeX + 14} ${shape.eyeY + 56}" fill="none" stroke="${color}" stroke-width="2.5" stroke-linecap="round"/>`;
            case CommunityAgentScope.PERSONA_LUCA:
                return `<g fill="${color}"><rect x="146" y="40" width="8" height="16" rx="2.5"/><rect x="158" y="29" width="8" height="27" rx="2.5"/><rect x="170" y="18" width="8" height="38" rx="2.5"/></g>`;
            case CommunityAgentScope.PERSONA_JULE:
                return `<path d="M160 16 C149 16 142 24 142 33 C142 45 160 60 160 60 C160 60 178 45 178 33 C178 24 171 16 160 16 Z" fill="${color}"/><circle cx="160" cy="33" r="6" fill="#fff" opacity=".85"/>`;
            case CommunityAgentScope.PERSONA_CHRIS:
                return `<path d="M160 58 C142 46 135 37 135 29 C135 21 141 16 148 16 C153 16 157 19 160 23 C163 19 167 16 172 16 C179 16 185 21 185 29 C185 37 178 46 160 58 Z" fill="${color}"/>`;
            case CommunityAgentScope.PERSONA_MAX:
                return `<path d="M141 16 H179 Q187 16 187 24 V40 Q187 48 179 48 H157 L146 59 L148 48 H141 Q133 48 133 40 V24 Q133 16 141 16 Z" fill="${color}"/><g fill="#fff" opacity=".9"><circle cx="149" cy="32" r="3"/><circle cx="160" cy="32" r="3"/><circle cx="171" cy="32" r="3"/></g>`;
            case CommunityAgentScope.PERSONA_LOUISE:
                return `<ellipse cx="92" cy="46" rx="42" ry="13" transform="rotate(-10 92 46)" fill="#ff6f61"/><circle cx="84" cy="30" r="5.5" fill="#ff6f61"/>`;
        }
        return "";
    }

    /**
     * Le cadre du corps : relevé à la main pour les six premiers dessins, lu sur les points du tracé pour les autres.
     */
    private static boxOf(shape: string): number[] {
        if (this.boxes[shape]) {
            return this.boxes[shape];
        }
        const numbers = (this.shape(shape).path.match(/-?\d+(\.\d+)?/g) ?? []).map(Number);
        const xs = numbers.filter((_, index) => index % 2 == 0);
        const ys = numbers.filter((_, index) => index % 2 == 1);
        this.boxes[shape] = [Math.min(...xs), Math.min(...ys), Math.max(...xs), Math.max(...ys)];
        return this.boxes[shape];
    }

    private static buildShapes(): void {
        let hexPoints = Array.from({length: 6}, (_, index) => {
            let angle = -Math.PI / 2 + index / 6 * Math.PI * 2;
            return [100 + 66 * Math.cos(angle), 106 + 66 * Math.sin(angle)];
        });
        this.shapes.set("hex", {path: this.roundedPolygon(hexPoints, 30), eyeY: 106, eyeX: 100});
        this.shapes.set("pebble", {path: this.polarPath(angle => [70 + 4 * Math.sin(angle * 2 + 1), 55 + 5 * Math.cos(angle + .4)], 100, 108), eyeY: 104, eyeX: 100});
        this.shapes.set("gem", {path: this.roundedPolygon([[64, 50], [136, 50], [166, 92], [100, 168], [34, 92]], 26), eyeY: 88, eyeX: 100});
        this.shapes.set("shield", {path: "M100 38 Q128 48 150 52 Q160 54 160 68 V100 C160 140 132 162 100 174 C68 162 40 140 40 100 V68 Q40 54 50 52 Q72 48 100 38 Z", eyeY: 98, eyeX: 100});
        this.shapes.set("bubble", {path: "M88 44 H112 Q160 44 160 90 V100 Q160 146 112 146 H92 L64 170 L68 140 Q40 128 40 100 V90 Q40 44 88 44 Z", eyeY: 94, eyeX: 100});
        this.shapes.set("square", {path: this.roundedPolygon([[46, 50], [154, 50], [154, 160], [46, 160]], 32), eyeY: 104, eyeX: 100});
        this.shapes.set("house", {path: this.roundedPolygon([[100, 38], [162, 92], [162, 166], [38, 166], [38, 92]], 24), eyeY: 118, eyeX: 100});
        this.shapes.set("capsule", {path: this.roundedPolygon([[30, 64], [170, 64], [170, 152], [30, 152]], 42), eyeY: 106, eyeX: 100});
        this.shapes.set("drop", {
            path: this.polarPath(angle => {
                const radius = 58 + 26 * Math.pow(Math.max(0, -Math.sin(angle)), 3);
                return [radius, radius];
            }, 100, 120), eyeY: 122, eyeX: 100
        });
        this.shapes.set("blob", {
            path: this.polarPath(angle => {
                let radius = 63 + 5 * Math.sin(3 * angle + .5) + 3 * Math.cos(2 * angle);
                return [radius, radius];
            }, 100, 106), eyeY: 104, eyeX: 100
        });
    }

    private static roundedPolygon(points: number[][], radius: number): string {
        let path = "";
        points.forEach((point, index) => {
            let previous = points[(index - 1 + points.length) % points.length];
            let next = points[(index + 1) % points.length];
            let toPrevious = Math.hypot(previous[0] - point[0], previous[1] - point[1]);
            let toNext = Math.hypot(next[0] - point[0], next[1] - point[1]);
            let cutPrevious = Math.min(radius, toPrevious / 2) / toPrevious;
            let cutNext = Math.min(radius, toNext / 2) / toNext;
            let start = [point[0] + (previous[0] - point[0]) * cutPrevious, point[1] + (previous[1] - point[1]) * cutPrevious];
            let end = [point[0] + (next[0] - point[0]) * cutNext, point[1] + (next[1] - point[1]) * cutNext];
            path += `${index ? "L" : "M"}${start[0].toFixed(1)} ${start[1].toFixed(1)} Q${point[0].toFixed(1)} ${point[1].toFixed(1)} ${end[0].toFixed(1)} ${end[1].toFixed(1)}`;
        });
        return path + "Z";
    }

    private static polarPath(radiusAt: (angle: number) => number[], centerX: number, centerY: number): string {
        let steps = 72;
        let path = "";
        for (let index = 0; index < steps; index++) {
            let angle = index / steps * Math.PI * 2;
            let radius = radiusAt(angle);
            path += (index ? "L" : "M") + (centerX + radius[0] * Math.cos(angle)).toFixed(1) + " " + (centerY + radius[1] * Math.sin(angle)).toFixed(1);
        }
        return path + "Z";
    }

    private static scaledAround(centerX: number, centerY: number, scale: number, shiftX: number, shiftY: number): string {
        return `translate(${(centerX + shiftX).toFixed(1)} ${(centerY + shiftY).toFixed(1)}) scale(${scale}) translate(${(-centerX).toFixed(1)} ${(-centerY).toFixed(1)})`;
    }

    private static hueShade(hex: string, deltaHue: number, deltaLightness: number, deltaSaturation: number = 0): string {
        let value = parseInt(hex.slice(1), 16);
        let red = (value >> 16 & 255) / 255;
        let green = (value >> 8 & 255) / 255;
        let blue = (value & 255) / 255;
        let max = Math.max(red, green, blue);
        let min = Math.min(red, green, blue);
        let lightness = (max + min) / 2;
        let hue = 0;
        let saturation = 0;
        if (max != min) {
            let delta = max - min;
            saturation = lightness > .5 ? delta / (2 - max - min) : delta / (max + min);
            hue = (max == red ? (green - blue) / delta + (green < blue ? 6 : 0) : max == green ? (blue - red) / delta + 2 : (red - green) / delta + 4) * 60;
        }
        let clamp = (number: number) => Math.max(4, Math.min(97, number));
        return `hsl(${((hue + deltaHue + 360) % 360).toFixed(0)} ${Math.max(0, Math.min(100, saturation * 100 + deltaSaturation)).toFixed(0)}% ${clamp(lightness * 100 + deltaLightness).toFixed(1)}%)`;
    }
}
